import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import { useCompare } from '@/components/CompareBar';
import { useTenant } from '@/contexts/TenantContext';
import { X, ShoppingCart } from 'lucide-react';
import { getDisplayPrice } from '@/utils/pricing';

/**
 * Side-by-side product comparison (up to 3 items).
 */
export default function ComparePage() {
  const { items, toggle, clear } = useCompare();
  const { tenantPath } = useTenant();

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <Breadcrumbs items={[{ label: 'Compare' }]} />
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Compare
            </h1>
            <p className="text-gray-500 text-sm mt-1">{items.length} of 3 items</p>
          </div>
          {items.length > 0 && (
            <button
              onClick={clear}
              className="text-sm font-medium text-gray-600 hover:text-gray-900"
              data-testid="compare-clear-all"
            >
              Clear all
            </button>
          )}
        </div>

        {items.length === 0 ? (
          <div className="text-center py-24 border-2 border-dashed border-gray-200 rounded-xl">
            <p className="text-gray-500 text-lg mb-4">No items to compare yet.</p>
            <Link
              to={tenantPath('/category/drivers')}
              className="inline-block bg-[#334155] text-white font-bold uppercase tracking-wider text-sm px-6 py-3 rounded-lg hover:bg-[#475569] transition-colors"
            >
              Browse drivers
            </Link>
          </div>
        ) : (
          <div className="grid gap-6" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
            {items.map((p) => (
              <div
                key={p.product_id}
                className="border border-gray-200 rounded-xl p-4 flex flex-col"
                data-testid={`compare-card-${p.product_id}`}
              >
                <div className="relative aspect-square bg-gray-50 rounded-lg overflow-hidden mb-4">
                  {p.image_url && (
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-contain" />
                  )}
                  <button
                    onClick={() => toggle(p)}
                    className="absolute top-2 right-2 bg-white rounded-full p-1.5 shadow-md text-gray-500 hover:text-gray-900"
                    aria-label="Remove"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-[11px] font-bold uppercase tracking-widest text-[#334155]">
                  {p.manufacturer}
                </p>
                <h3 className="text-sm font-semibold text-gray-900 mb-2 line-clamp-2">{p.name}</h3>
                <p className="text-xl font-bold text-[#334155] mb-4">${getDisplayPrice(p).toFixed(2)}</p>
                <dl className="text-xs text-gray-600 space-y-2 mb-4 pt-2 border-t border-gray-100">
                  <div className="flex justify-between">
                    <dt className="text-gray-400">Category</dt>
                    <dd className="font-medium text-gray-800">{p.category}</dd>
                  </div>
                </dl>
                <Link
                  to={tenantPath(`/product/${p.product_id}`)}
                  className="mt-auto block text-center bg-[#334155] text-white text-sm font-bold uppercase tracking-wider py-2.5 rounded-lg hover:bg-[#475569] transition-colors"
                >
                  <ShoppingCart className="inline w-4 h-4 mr-1.5 -mt-0.5" />
                  View
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
